import React, { useState, useEffect, useContext } from "react";
import { Form } from "reactstrap";
import { Trash, Plus } from "react-feather";
import { getCourses } from "../../../../Api/apiUtils";
import { ConfigContext } from "../../../../Api/global/ApiContext";
import { toast } from "react-toastify";
import { MixpanelEvents, trackEvent } from "../../../../utils/mixpanelTracking";
import { Button, EmptyState, Field, Loader, Select, Table, useConfirm } from "../../../../ui";
import "./courseTagging.scss";

interface TeacherCourse {
  courseName: string;
  teacherId: string;
  level: string;
  courseId: string;
  teacherCourseLevelId: number;
}

interface Course {
  id: string;
  v2Id: string;
  name: string;
  tags: {
    name: string;
    levelId: string;
    lmsCourseId: string;
  }[];
}

interface ApiResponse {
  getcourseList?: TeacherCourse[];
}

interface CourseTaggingProps {
  teacherId: string;
}

const CourseTagging: React.FC<CourseTaggingProps> = ({ teacherId }) => {
  const config = useContext(ConfigContext);
  const { grmpcourses = [] } = config || {};
  console.log("grmp", grmpcourses);
  const [teacherCourses, setTeacherCourses] = useState<TeacherCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { confirm, dialog } = useConfirm();

  useEffect(() => {
    fetchTeacherCourses();
  }, [teacherId]);
  const fetchTeacherCourses = async () => {
    try {
      setLoading(true);
      const response = await getCourses([
        {
          teacherId: teacherId,
          status: "getteachermapppingcourse"
        }
      ]);
      
      const responseData = response.data as ApiResponse;
      
      if (responseData?.getcourseList && Array.isArray(responseData.getcourseList)) {
        setTeacherCourses(responseData.getcourseList);
      } else {
        setTeacherCourses([]); // Ensure empty array if no courses
      }
    } catch (error) {
      console.error("Failed to fetch courses:", error);
      toast.error("Failed to fetch courses");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = async (courseId: string, level: string) => {
    try {
      setLoading(true);
      await getCourses([{
        courseId: courseId,
        teacherId: teacherId,
        levels: [level],
        status: "deletecourse"
      }]);
      toast.success("Course level deleted successfully");
      
      // Optimistically update the UI
      setTeacherCourses(prev => 
        prev.filter(course => 
          !(course.courseId === courseId && course.level === level)
        )
      );
      
      // Then refetch to ensure consistency
      await fetchTeacherCourses();
    } catch (error) {
      console.error("Failed to delete course level:", error);
      toast.error("Failed to delete course level");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCourse = async () => {
    if (!selectedCourse) {
      setError("Please select a course");
      return;
    }
  
    try {
      setSubmitting(true);
      setError("");
      
      const course = grmpcourses.find((c: any) => c.v2Id === selectedCourse);
      if (!course) return;
  
      const levelsToSend = course.tags?.length > 0 ? selectedLevels : ["Preparatory"];
      // First-time tagging → notifySlack=true; later adds → false.
      const notifySlack = teacherCourses.length === 0;

      const response = await getCourses(
        [{
          courseId: course.v2Id,
          teacherId: teacherId,
          levels: levelsToSend,
          status: "addcourse"
        }],
        { notifySlack },
      );
  
  trackEvent(MixpanelEvents.COURSE_TAGGING, {
         page:"Course Tagging student profile",
         courseName:course.name,
         courseId: course.v2Id,
         teacherId: teacherId,
         levels: levelsToSend,
     
        });
      if (!response.data?.errors) {
        toast.success("Course added successfully");
      } else {
   
        const specificError = response.data.errors.find((err: string) => 
          err.includes("Mapping already exists for courseId") && err.includes("level:")
        );
        

        toast.warning( "Mapping already exists for this course");
      }
      
      await fetchTeacherCourses();
      setSelectedCourse("");
      setSelectedLevels([]);
    } catch (error: any) {
      console.error("Failed to add course:", error);

      if (!error.response?.data?.errors) {
        toast.error("Failed to add course. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };
  const handleCourseSelect = (courseId: string) => {
    setSelectedCourse(courseId);
    setSelectedLevels([]);
  };

  const handleLevelToggle = (level: string) => {
    setSelectedLevels(prev => 
      prev.includes(level) 
        ? prev.filter(l => l !== level) 
        : [...prev, level]
    );
  };



  const getSelectedCourse = () => {
    return grmpcourses.find((c:any)=> c.v2Id === selectedCourse);
  };

  const courseHasLevels = () => {
    const course = getSelectedCourse();
    return course?.tags && course.tags.length > 0;
  };

  // If the course is an offline variant, show the offline name from grmpcourses
  const getDisplayCourseName = (courseId: string, courseName: string) => {
    if (!courseId || !Array.isArray(grmpcourses)) return courseName;

    const offlineCourse = (grmpcourses as any[]).find(
      (c: any) =>
        c?.v2Id === courseId &&
        typeof c.name === "string" &&
        c.name.toLowerCase().includes("offline")
    );

    return offlineCourse?.name || courseName;
  };

  const confirmDeleteCourse = async (courseId: string, level: string, courseLabel: string) => {
    const ok = await confirm({
      title: "Remove course level?",
      message: `${courseLabel} — ${level || "No level"} will be untagged from this teacher.`,
      confirmLabel: "Remove",
      tone: "danger",
    });
    if (!ok) return;
    await handleDeleteCourse(courseId, level);
  };

  if (loading && teacherCourses.length === 0) {
    return (
      <>
        <Loader label="Loading tagged courses…" />
        {dialog}
      </>
    );
  }

  const selectedCourseTags = getSelectedCourse()?.tags || [];

  return (
    <div className="tp-tagging">
      {/* Add Course Section */}
      <Form className="tp-tagging__form">
        <div className="tp-tagging__fields">
          <Field label="Select Course" id="courseSelect" className="tp-tagging__course">
            {(control) => (
              <Select
                {...control}
                value={selectedCourse}
                onChange={(e) => handleCourseSelect(e.target.value)}
                options={[
                  { value: "", label: "Select a course" },
                  ...grmpcourses.map((course: any) => ({ value: course.v2Id, label: course.name })),
                ]}
              />
            )}
          </Field>

          {selectedCourse && courseHasLevels() && (
            <fieldset className="tp-tagging__levels">
              <legend className="tp-tagging__legend">Select Levels</legend>
              <div className="tp-tagging__level-list">
                {selectedCourseTags.map((tag: any) => (
                  <div className="form-check" key={tag.levelId}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`level-${tag.levelId}`}
                      checked={selectedLevels.includes(tag.name)}
                      onChange={() => handleLevelToggle(tag.name)}
                    />
                    <label className="form-check-label" htmlFor={`level-${tag.levelId}`}>
                      {tag.name}
                    </label>
                  </div>
                ))}
              </div>
            </fieldset>
          )}
          {/*
          {selectedCourse && !courseHasLevels() && (
            <p className="ds-text-muted">This course doesn't have any levels</p>
          )} */}
        </div>

        {error && <span className="tp-tagging__error" role="alert">{error}</span>}

        <div>
          <Button
            variant="primary"
            icon={<Plus size={16} aria-hidden />}
            onClick={handleAddCourse}
            loading={submitting}
            disabled={submitting || !selectedCourse || (courseHasLevels() && selectedLevels.length === 0)}
          >
            Add Course
          </Button>
        </div>
      </Form>

      {teacherCourses.length > 0 ? (
        <div className="tp-tagging__scroll">
          <Table caption="Courses tagged to this teacher">
            <thead>
              <tr>
                <th>Course</th>
                <th>Level</th>
                <th className="tp-tagging__action-col">Action</th>
              </tr>
            </thead>
            <tbody>
              {Array.from(new Set(teacherCourses.map(item => item.courseName))).map((courseName, courseIndex) => {
                const levelOrder = ["Preparatory", "Intermediate", "Proficient", "Advanced"];

                const courseLevels = teacherCourses
                  .filter(item => item.courseName === courseName)
                  .sort((a, b) => {
                    return levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level);
                  });

                const courseIdForGroup = courseLevels[0]?.courseId || "";
                const displayName = getDisplayCourseName(courseIdForGroup, courseName as string);

                return (
                  <React.Fragment key={courseIndex}>
                    {courseLevels.map((item, levelIndex) => (
                      <tr key={`${courseIndex}-${levelIndex}`}>
                        {levelIndex === 0 ? (
                          <td rowSpan={courseLevels.length} className="ds-strong">
                            {displayName}
                          </td>
                        ) : null}
                        <td>{item.level || 'No level'}</td>
                        <td className="tp-tagging__action-col">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="tp-tagging__remove"
                            aria-label={`Remove ${displayName} ${item.level || 'No level'}`}
                            icon={<Trash size={16} aria-hidden />}
                            onClick={() => confirmDeleteCourse(item.courseId, item.level, displayName)}
                            disabled={loading}
                          />
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      ) : (
        <EmptyState
          title="No courses assigned yet"
          description="Pick a course and its levels above, then select Add Course."
        />
      )}
      {dialog}
    </div>
  );
};

export default CourseTagging;